import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Shield, AlertTriangle, CheckCircle, XCircle, Eye, Clock, FileText, RefreshCw, LayoutTemplate } from 'lucide-react';
import api from '../api';
import toast from 'react-hot-toast';

interface SecurityOverview {
  totalDocuments: number;
  blockedDocuments: number;
  sanitizedDocuments: number;
  approvedDocuments: number;
  pendingDocuments: number;
  securityScore: number;
  lastScanDate: string;
}

interface SecurityActivity {
  action_type: string;
  risk_level: string;
  item_type: string;
  item_name: string;
  created_at: string;
  pii_types: string[];
}

interface SecurityAlert {
  item_type: 'document' | 'template';
  item_id: number;
  item_name: string;
  pii_status: string;
  pii_risk_level: string;
  pii_types: string[];
  alert_level: 'critical' | 'high' | 'medium' | 'low';
  alert_message: string;
}

interface SecurityDashboardProps {
  projectId: number;
  className?: string;
  // NEW: Add refresh trigger props
  refreshTrigger?: number;
  onDataChange?: (overview: SecurityOverview) => void;
  onSecurityReview?: (documentId: number, action: 'approve' | 'block' | 'sanitize') => void;
}

const SecurityDashboard: React.FC<SecurityDashboardProps> = ({ 
  projectId, 
  className = "",
  refreshTrigger = 0,
  onDataChange,
  onSecurityReview
}) => {
  const [overview, setOverview] = useState<SecurityOverview | null>(null);
  const [activities, setActivities] = useState<SecurityActivity[]>([]);
  const [alerts, setAlerts] = useState<SecurityAlert[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedTab, setSelectedTab] = useState<'overview' | 'alerts' | 'activity'>('overview');
  const [lastRefresh, setLastRefresh] = useState<number>(Date.now());
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  
  // NEW: Add retry and network error state
  const [retryCount, setRetryCount] = useState(0);
  const [networkError, setNetworkError] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<'connected' | 'retrying' | 'failed'>('connected');

  // NEW: Sleep function for retry delays
  const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

  // NEW: Enhanced retry logic with exponential backoff
  const retryWithBackoff = async (fn: () => Promise<any>, maxRetries = 3) => {
    for (let i = 0; i < maxRetries; i++) {
      try {
        const result = await fn();
        setNetworkError(false);
        setConnectionStatus('connected');
        return result;
      } catch (error: any) {
        console.warn(`🔄 SecurityDashboard: Attempt ${i + 1} failed:`, error.message);
        
        // Check if it's a network error
        const isNetworkError = error.message?.includes('Network Error') || 
                              error.message?.includes('ERR_NETWORK') ||
                              error.message?.includes('Failed to fetch') ||
                              error.message?.includes('ERR_FAILED') ||
                              error.code === 'ERR_NETWORK' ||
                              error.code === 'NETWORK_ERROR';
        
        if (isNetworkError) {
          setNetworkError(true);
          setConnectionStatus('retrying');
        }
        
        if (i === maxRetries - 1) {
          setConnectionStatus('failed');
          throw error; // Re-throw on final attempt
        }
        
        // Exponential backoff: 2s, 4s, 8s
        const delay = Math.pow(2, i + 1) * 1000;
        console.log(`⏳ SecurityDashboard: Retrying in ${delay}ms...`);
        await sleep(delay);
      }
    }
  };

  // ENHANCED: Data loading with robust retry logic
  const loadSecurityData = useCallback(async (forceRefresh = false) => {
    if (!projectId) return;

    // Prevent multiple simultaneous loads unless forced
    if (loading && !forceRefresh) {
      console.log('🔒 SecurityDashboard: Load already in progress, skipping');
      return;
    }

    try {
      // Set loading states
      if (forceRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      
      console.log(`🔒 SecurityDashboard: Loading data for project ${projectId}...`);
      
      // SIMPLIFIED: Single API call with basic timeout
      const [dashboardResponse, alertsResponse] = await Promise.all([
        api.get(`/api/documents/security/dashboard/${projectId}`, {
          params: forceRefresh ? { _t: Date.now() } : {},
          timeout: 10000
        }).catch(error => {
          console.warn('Dashboard endpoint failed:', error.message);
          // Return fallback data instead of throwing
          return {
            data: {
              overview: {
                totalDocuments: 0,
                blockedDocuments: 0,
                sanitizedDocuments: 0,
                approvedDocuments: 0,
                pendingDocuments: 0,
                securityScore: 100,
                lastScanDate: new Date().toISOString()
              },
              recentActivities: []
            }
          };
        }),
        
        api.get(`/api/documents/security/alerts/${projectId}`, {
          params: forceRefresh ? { _t: Date.now() } : {},
          timeout: 10000
        }).catch(error => {
          console.warn('Alerts endpoint failed:', error.message);
          // Return fallback data instead of throwing
          return { data: { alerts: [] } };
        })
      ]);

      // Process responses
      const newOverview = dashboardResponse.data.overview;
      const newActivities = dashboardResponse.data.recentActivities || [];
      const newAlerts = alertsResponse.data.alerts || [];

      // Update state
      setOverview(newOverview);
      setActivities(newActivities);
      setAlerts(newAlerts);
      setLastRefresh(Date.now());
      setNetworkError(false); // Clear any previous network errors
      setConnectionStatus('connected');

      // Notify parent component of data changes
      if (onDataChange && newOverview) {
        onDataChange(newOverview);
      }

      console.log(`✅ SecurityDashboard: Data loaded successfully`, {
        totalDocuments: newOverview?.totalDocuments,
        activitiesCount: newActivities.length,
        alertsCount: newAlerts.length
      });

    } catch (error: any) {
      console.error('❌ SecurityDashboard: Failed to load security data:', error);
      
      // Set connection status but don't trigger infinite retries
      setNetworkError(true);
      setConnectionStatus('failed');
      
      // Only show error toast for manual refresh
      if (forceRefresh) {
        toast.error('Failed to load security dashboard', { duration: 3000 });
      }
      
      // Set fallback data to prevent UI breaking
      if (!overview) {
        setOverview({
          totalDocuments: 0,
          blockedDocuments: 0,
          sanitizedDocuments: 0,
          approvedDocuments: 0,
          pendingDocuments: 0,
          securityScore: 100,
          lastScanDate: new Date().toISOString()
        });
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [projectId, onDataChange, loading]);

  // Initial load
  useEffect(() => {
    if (projectId) {
      console.log(`🔒 SecurityDashboard: Initial load for project ${projectId}`);
      loadSecurityData();
    }
  }, [projectId]);

  // ENHANCED: Handle refresh trigger with increased delay for stability
  useEffect(() => {
    if (refreshTrigger > 0) {
      console.log(`🔄 SecurityDashboard: Refresh triggered (${refreshTrigger})`);
      
      // INCREASED delay to prevent conflicts with document processing
      const timer = setTimeout(() => {
        loadSecurityData(true);
      }, 3000); // Increased from 500ms to 3 seconds
      
      return () => clearTimeout(timer);
    }
  }, [refreshTrigger, loadSecurityData]);

  // NEW: Auto-retry mechanism for network errors
  // useEffect(() => {
  //   if (networkError && retryCount < 5 && connectionStatus === 'retrying') {
  //     console.log(`🔄 SecurityDashboard: Auto-retry due to network error (attempt ${retryCount + 1}/5)`);
  //     const timer = setTimeout(() => {
  //       loadSecurityData(true);
  //     }, 8000); // Wait 8 seconds before auto-retry

  //     return () => clearTimeout(timer);
  //   }
  // }, [networkError, retryCount, connectionStatus, loadSecurityData]);

  // Auto-refresh functionality (your existing code)
  useEffect(() => {
    if (!autoRefresh || !projectId) return;

    const interval = setInterval(() => {
      loadSecurityData(true);
    }, 30000); 

    return () => clearInterval(interval);
  }, [autoRefresh, projectId, loadSecurityData]);

  // Manual refresh handler (your existing code)
  const handleManualRefresh = useCallback(() => {
    console.log(`🔄 SecurityDashboard: Manual refresh triggered`);
    loadSecurityData(true);
  }, [loadSecurityData]);

  // Handle bulk security review (your existing code with small enhancement)
  const handleBulkReview = useCallback(async (items: {type: string, id: number}[], action: string) => {
    try {
      setRefreshing(true);
      
      // If we have a single document and onSecurityReview handler, use it
      if (items.length === 1 && items[0].type === 'document' && onSecurityReview) {
        console.log(`🔄 SecurityDashboard: Using parent handler for document ${items[0].id}`);
        await onSecurityReview(items[0].id, action as 'approve' | 'block' | 'sanitize');
        
        // CRITICAL: Don't refresh data - let parent handle it completely
        console.log(`✅ SecurityDashboard: Parent handler completed, parent will handle refresh`);
        return; // Exit without calling loadSecurityData
      }
      
      // For bulk operations (multiple items), use the bulk API
      console.log(`🔄 SecurityDashboard: Using bulk API for ${items.length} items`);
      await api.post('/api/security/bulk-review', {
        items,
        action,
        notes: `Bulk ${action} via security dashboard`
      });
      
      toast.success(`Bulk ${action} completed successfully`);
      
      // ONLY refresh for actual bulk operations (multiple items)
      if (items.length > 1) {
        console.log(`🔄 SecurityDashboard: Refreshing data for bulk operation`);
        // Use a delay to let the backend process the bulk updates
        setTimeout(async () => {
          await loadSecurityData(true);
        }, 2000);
      }
      
    } catch (error: any) {
      console.error('SecurityDashboard: Bulk review failed:', error);
      toast.error('Bulk review failed');
    } finally {
      setRefreshing(false);
    }
  }, [onSecurityReview, loadSecurityData]);

  // Memoized security score styling (your existing code)
  const securityScoreStyle = useMemo(() => {
    if (!overview) return { color: 'text-gray-600', bg: 'bg-gray-100' };
    
    const score = overview.securityScore;
    if (score >= 95) return { color: 'text-green-600', bg: 'bg-green-100' };
    if (score >= 85) return { color: 'text-yellow-600', bg: 'bg-yellow-100' };
    if (score >= 70) return { color: 'text-orange-600', bg: 'bg-orange-100' };
    return { color: 'text-red-600', bg: 'bg-red-100' };
  }, [overview]);

  // Memoized security status text (your existing code)
  const securityStatusText = useMemo(() => {
    if (!overview) return 'Loading...';
    
    const score = overview.securityScore;
    if (score >= 95) return 'Excellent security posture';
    if (score >= 85) return 'Good security with minor improvements needed';
    if (score >= 70) return 'Moderate security requiring attention';
    return 'Poor security requiring immediate action';
  }, [overview]);

  const getRiskLevelBadge = useCallback((riskLevel: string) => {
    const styles = {
      critical: 'bg-red-100 text-red-800',
      high: 'bg-orange-100 text-orange-800',
      medium: 'bg-yellow-100 text-yellow-800',
      low: 'bg-green-100 text-green-800'
    };
    
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[riskLevel as keyof typeof styles] || styles.low}`}>
        {riskLevel.toUpperCase()}
      </span>
    );
  }, []);

  const formatDate = useCallback((dateString: string): string => {
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }, []);

  // Loading skeleton component (your existing code)
  const LoadingSkeleton = () => (
    <div className="animate-pulse space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="bg-gray-200 rounded-lg h-24"></div>
        ))}
      </div>
      <div className="bg-gray-200 rounded-lg h-16"></div>
    </div>
  );

  // ENHANCED: Loading state with connection status
  if (loading && !overview) {
    return (
      <div className={`bg-white rounded-lg shadow p-6 ${className}`}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            <Shield className="w-5 h-5 text-super-dark-purple" />
            <h3 className="text-lg font-semibold text-super-dark-purple">Security Dashboard</h3>
            {/* NEW: Connection status indicator */}
            {networkError && (
              <span className="text-xs text-orange-600 bg-orange-100 px-2 py-1 rounded">
                Connection issues - retrying...
              </span>
            )}
          </div>
          <div className="animate-spin">
            <RefreshCw className="w-4 h-4 text-gray-400" />
          </div>
        </div>
        <LoadingSkeleton />
      </div>
    );
  }

  return (
    <div className={`bg-white rounded-lg shadow ${className}`}>
      {/* Enhanced Header with connection status */}
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Shield className="w-5 h-5 text-super-dark-purple" />
            <h3 className="text-lg font-semibold text-super-dark-purple">Security Dashboard</h3>
            {/* ENHANCED: Last refresh indicator with connection status */}
            <span className="text-xs text-gray-500">
              Updated {formatDate(new Date(lastRefresh).toISOString())}
            </span>
            {/* NEW: Connection status indicators */}
            {connectionStatus === 'retrying' && (
              <span className="text-xs text-orange-600 bg-orange-100 px-2 py-1 rounded">
                Retrying...
              </span>
            )}
            {connectionStatus === 'failed' && (
              <span className="text-xs text-red-600 bg-red-100 px-2 py-1 rounded">
                Connection failed
              </span>
            )}
          </div>
          
          <div className="flex items-center space-x-2">
            {/* Auto-refresh toggle (your existing code) */}
            <button
              onClick={() => setAutoRefresh(!autoRefresh)}
              className={`px-2 py-1 rounded text-xs ${
                autoRefresh ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
              }`}
              title={autoRefresh ? 'Auto-refresh enabled' : 'Auto-refresh disabled'}
            >
              Auto
            </button>
            
            {/* Manual refresh button (your existing code) */}
            <button
              onClick={handleManualRefresh}
              disabled={refreshing}
              className="p-1 rounded hover:bg-gray-100 disabled:opacity-50"
              title="Refresh data"
            >
              <RefreshCw className={`w-4 h-4 text-gray-600 ${refreshing ? 'animate-spin' : ''}`} />
            </button>
            
            {overview && (
              <div className={`px-3 py-1 rounded-full ${securityScoreStyle.bg}`}>
                <span className={`text-sm font-bold ${securityScoreStyle.color}`}>
                  Score: {overview.securityScore}%
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Tab Navigation (your existing code) */}
        <div className="flex space-x-6 mt-4">
          <button
            onClick={() => setSelectedTab('overview')}
            className={`pb-2 text-sm font-medium border-b-2 transition-colors ${
              selectedTab === 'overview'
                ? 'border-super-dark-purple text-super-dark-purple'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Overview
          </button>
          <button
            onClick={() => setSelectedTab('alerts')}
            className={`pb-2 text-sm font-medium border-b-2 transition-colors ${
              selectedTab === 'alerts'
                ? 'border-super-dark-purple text-super-dark-purple'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Alerts ({alerts.length})
          </button>
          <button
            onClick={() => setSelectedTab('activity')}
            className={`pb-2 text-sm font-medium border-b-2 transition-colors ${
              selectedTab === 'activity'
                ? 'border-super-dark-purple text-super-dark-purple'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Recent Activity
          </button>
        </div>
      </div>

      {/* Content - Keep all your existing tab content exactly as-is */}
      <div className="p-6">
        {selectedTab === 'overview' && overview && (
          <div className="space-y-6">
            {/* Security Metrics */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center p-4 bg-green-50 rounded-lg transition-all hover:shadow-md">
                <CheckCircle className="w-8 h-8 text-green-600 mx-auto mb-2" />
                <div className="text-2xl font-bold text-green-700">{overview.approvedDocuments}</div>
                <div className="text-sm text-green-600">Approved</div>
              </div>
              
              <div className="text-center p-4 bg-yellow-50 rounded-lg transition-all hover:shadow-md">
                <Eye className="w-8 h-8 text-yellow-600 mx-auto mb-2" />
                <div className="text-2xl font-bold text-yellow-700">{overview.sanitizedDocuments}</div>
                <div className="text-sm text-yellow-600">Sanitized</div>
              </div>
              
              <div className="text-center p-4 bg-red-50 rounded-lg transition-all hover:shadow-md">
                <XCircle className="w-8 h-8 text-red-600 mx-auto mb-2" />
                <div className="text-2xl font-bold text-red-700">{overview.blockedDocuments}</div>
                <div className="text-sm text-red-600">Blocked</div>
              </div>
              
              <div className="text-center p-4 bg-blue-50 rounded-lg transition-all hover:shadow-md">
                <FileText className="w-8 h-8 text-blue-600 mx-auto mb-2" />
                <div className="text-2xl font-bold text-blue-700">{overview.totalDocuments}</div>
                <div className="text-sm text-blue-600">Total Documents</div>
              </div>
            </div>

            {/* Enhanced Security Score Visualization */}
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-medium text-gray-900">Security Score Breakdown</h4>
                {refreshing && (
                  <div className="flex items-center text-xs text-gray-500">
                    <RefreshCw className="w-3 h-3 animate-spin mr-1" />
                    Updating...
                  </div>
                )}
              </div>
              <div className="w-full bg-gray-200 rounded-full h-4 mb-2">
                <div 
                  className={`h-4 rounded-full transition-all duration-500 ${
                    overview.securityScore >= 95 ? 'bg-green-500' :
                    overview.securityScore >= 85 ? 'bg-yellow-500' :
                    overview.securityScore >= 70 ? 'bg-orange-500' : 'bg-red-500'
                  }`}
                  style={{ width: `${overview.securityScore}%` }}
                ></div>
              </div>
              <div className="text-sm text-gray-600">
                {securityStatusText}
              </div>
            </div>
          </div>
        )}

        {selectedTab === 'alerts' && (
          <div className="space-y-4">
            {alerts.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Shield className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p>No security alerts at this time</p>
              </div>
            ) : (
              <>
                {/* Enhanced Bulk Actions */}
                <div className="flex justify-between items-center mb-4">
                  <div className="text-sm text-gray-600">
                    {alerts.length} alert{alerts.length !== 1 ? 's' : ''} requiring attention
                  </div>
                  <div className="flex space-x-2">
                    <button 
                      onClick={() => {
                        const criticalAlerts = alerts
                          .filter(a => a.alert_level === 'critical')
                          .map(a => ({ type: a.item_type, id: a.item_id }));
                        if (criticalAlerts.length > 0) {
                          handleBulkReview(criticalAlerts, 'approve');
                        }
                      }}
                      disabled={refreshing}
                      className="px-3 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700 disabled:opacity-50"
                    >
                      Approve All Critical
                    </button>
                  </div>
                </div>

                {alerts.map((alert) => (
                  <div key={`${alert.item_type}-${alert.item_id}`} 
                       className="border rounded-lg p-4 hover:bg-gray-50 transition-colors">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          {alert.item_type === 'document' ? (
                            <FileText className="w-5 h-5 text-blue-600" />
                          ) : (
                            <LayoutTemplate className="w-5 h-5 text-purple-600" />
                          )}
                          <span className="font-medium text-gray-900">{alert.item_name}</span>
                          {getRiskLevelBadge(alert.alert_level)}
                        </div>
                        
                        <p className="text-sm text-gray-600 mb-2">{alert.alert_message}</p>
                        
                        {alert.pii_types.length > 0 && (
                          <div className="flex flex-wrap gap-1">
                            {alert.pii_types.map((type) => (
                              <span key={type} className="px-2 py-1 bg-gray-200 text-gray-700 rounded text-xs">
                                {type.replace('_', ' ').toUpperCase()}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                      
                      <div className="flex space-x-2 ml-4">
                        <button 
                          onClick={() => handleBulkReview([{type: alert.item_type, id: alert.item_id}], 'approve')}
                          disabled={refreshing}
                          className="px-3 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700 disabled:opacity-50"
                        >
                          Approve
                        </button>
                        <button 
                          onClick={() => handleBulkReview([{type: alert.item_type, id: alert.item_id}], 'block')}
                          disabled={refreshing}
                          className="px-3 py-1 bg-red-600 text-white rounded text-sm hover:bg-red-700 disabled:opacity-50"
                        >
                          Block
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>
        )}

        {selectedTab === 'activity' && (
          <div className="space-y-3">
            {activities.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Clock className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p>No recent security activities</p>
              </div>
            ) : (
              activities.map((activity, index) => (
                <div key={index} className="flex items-center space-x-3 p-3 bg-gray-50 rounded">
                  <div className={`w-2 h-2 rounded-full ${
                    activity.action_type === 'block' ? 'bg-red-500' :
                    activity.action_type === 'sanitize' ? 'bg-yellow-500' : 'bg-green-500'
                  }`}></div>
                  
                  <div className="flex-1">
                    <div className="flex items-center space-x-2">
                      <span className="text-sm font-medium text-gray-900">
                        {activity.action_type.replace('_', ' ').toUpperCase()}
                      </span>
                      <span className="text-sm text-gray-600">•</span>
                      <span className="text-sm text-gray-600">{activity.item_name}</span>
                      {getRiskLevelBadge(activity.risk_level)}
                    </div>
                    
                    {activity.pii_types.length > 0 && (
                      <div className="text-xs text-gray-500 mt-1">
                        PII Types: {activity.pii_types.join(', ')}
                      </div>
                    )}
                  </div>
                  
                  <div className="text-xs text-gray-500">
                    {formatDate(activity.created_at)}
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SecurityDashboard;